import {
  BaseEntityDataDto,
  BaseEntityDataInputDto,
  BaseEntityDto,
  BaseEntityFullRefDto,
  BaseEntityRefDto,
  EntityFormType,
  IAssetSourceEntityDto,
  IHasGlobalNumberDto,
  IHasLocalNumberDto,
  IMightHaveDataGrantsBriefDto,
  IMightHaveDataGrantsDto,
  IMightHaveDepartmentsDto,
  IMightHaveImportMetaDto,
  IMightHaveLocationsDto,
  IMightHaveOriginDto,
  IMightHavePoolsDto,
  IMightHaveTagsDto,
  IMightHaveTenantDto,
  IMightHaveTenantRequestsDto,
  IMightHaveUnseenInfoDto,
} from "@/core/api/generated/v1";

/** Any entity DTO type according to @type EntityFormType */
export type BaseEntityDtoAny =
  | BaseEntityDto
  | BaseEntityRefDto
  | BaseEntityFullRefDto
  | BaseEntityDataDto
  | BaseEntityDataInputDto;

/** All entity DTO types according to @type EntityFormType */
export type BaseEntityDtoAll = BaseEntityDto &
  BaseEntityRefDto &
  BaseEntityFullRefDto &
  BaseEntityDataDto &
  BaseEntityDataInputDto;

/** Any entity DTO type. */
export type BaseEntityDataDtoAny = BaseEntityDataDto | BaseEntityDataInputDto;

/** All entity DTO types. */
export type BaseEntityDataDtoAll = BaseEntityDataDto & BaseEntityDataInputDto;

export const EntityFormMode = {
  Create: "Create",
  Update: "Update",
} as const;
export type EntityFormMode = (typeof EntityFormMode)[keyof typeof EntityFormMode];

export const EntityDefaultValuesMatchStatus = {
  Matched: "Matched",
  Prefilled: "Prefilled",
  NoMatch: "NoMatch",
} as const;
export type EntityDefaultValuesMatchStatus =
  (typeof EntityDefaultValuesMatchStatus)[keyof typeof EntityDefaultValuesMatchStatus];

/** Source of entity input form default values. */
export const EntityDefaultValuesSourceType = {
  // /** Entered by user. */
  // Manual: "Manual",
  // /** Auto prefilled data. */
  // Prefill: "Prefill",
  /** Values from data extraction. */
  Extraction: "Extraction",
} as const;
export type EntityDefaultValuesSourceType =
  (typeof EntityDefaultValuesSourceType)[keyof typeof EntityDefaultValuesSourceType];

/** Meta about entity input form default values. */
export interface EntityDefaultValuesMeta {
  /** @default EntityDefaultValuesSourceType.Manual */
  sourceType?: EntityDefaultValuesSourceType;
  /** Entity id that is matched by extracted data. */
  matchedId?: Nil<string>;
}

/** Entity input form base default values. */
export interface BaseEntityDefaultValues {
  meta?: Nil<EntityDefaultValuesMeta>;
}

/** Info about entity input form. */
export interface EntityInputFormInfo {
  entityId: Nil<string>;
  mode: EntityFormMode;
  isCreate: boolean;
  isUpdate: boolean;
}

/** Meta about entity forms (see @type EntityFormType). */
export interface EntityFormMeta {
  formType: Nil<EntityFormType>;
  entity: Nil<BaseEntityDto>;
  entityRef: Nil<BaseEntityRefDto>;
  entityFullRef: Nil<BaseEntityFullRefDto>;
  entityData: Nil<BaseEntityDataDto>;
  isEntity: boolean;
  isRef: boolean;
  isFullRef: boolean;
  isData: boolean;
  dataMeta: Nil<EntityDataMeta>;
}

/** Meta about entity data (see @type EntityFormType.Data). */
export interface EntityDataMeta extends BaseEntityDataDto {
  /** If @type EntityDataOriginMode.Ref. */
  isRef: boolean;
  /** If @type EntityDataOriginMode.Inline. */
  isInline: boolean;
}

export type EntityMeta = { formMeta: EntityFormMeta } & BaseEntityDto &
  IMightHaveTenantDto &
  IHasGlobalNumberDto &
  IHasLocalNumberDto &
  IMightHaveImportMetaDto &
  ((IMightHaveDataGrantsDto | IMightHaveDataGrantsBriefDto) & {
    grantsMeta?: (
      | IMightHaveDataGrantsDto["grantsMeta"]
      | IMightHaveDataGrantsBriefDto["grantsMeta"]
    ) & {
      isIssuedByMe: boolean;
      isConsumedByMe: boolean;
    };
  }) &
  IMightHaveTenantRequestsDto &
  IAssetSourceEntityDto &
  // todo: remove department when Dtos will have departmentIds
  IMightHaveDepartmentsDto &
  // todo: remove location when Dtos will have locationIds
  IMightHaveLocationsDto &
  IMightHavePoolsDto &
  IMightHaveTagsDto &
  IMightHaveUnseenInfoDto &
  IMightHaveOriginDto & {
    externalNumber: string | undefined;
  };

/** Info about entity affiliation to tenant, departments and locations. */
export type EntityAffiliationInfo = IMightHaveTenantDto &
  IMightHaveDepartmentsDto &
  IMightHaveLocationsDto & {
    tenantId: Nil<string>;
    departmentIds: Nil<string[]>;
    locationIds: Nil<string[]>;
  };
